import {defineStore} from 'pinia';
import type {UserResponse, Contract, UserConsents, UserRole, UserState} from '@/ts/types/dto/user-dto';
import type {EducationLevelDTO, UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {GenderDTO} from '@/ts/types/dto/gender-dto';
import type {ContentHomeResponse} from '@/ts/types/dto/home-dto';
import {useBookmarksStore} from '@/ts/store/bookmarks-store';
import {useWeightTrackingStore} from '@/ts/store/weight-tracking.store';
import {useTourStore} from '@/ts/store/tour-store';
import {useUserPreferencesStore} from '@/ts/store/user-preferences.store';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';

/** Normalized user profile data derived from the API response */
export interface UserData {
  id: UuidDTO;
  email: string;
  name: string;
  role: UserRole;
  gender: GenderDTO;
  countryCode: string;
  birthYear: number;
  educationLevel: EducationLevelDTO;
  inProgressInterventionId: UuidDTO | null;
  marketingCodeExternalId: UuidDTO;
  registrationDate: string;
  activeContract?: Contract | null;
  lastContract?: Contract | null;
  userState?: UserState;
  /** Whether the account has a password of its own; absent until the backend that sends it lands */
  hasPassword?: boolean;
  isEligibleForPurchase: boolean;
  organization?: {
    id: UuidDTO;
    name: string;
    createDate: string;
  };
  onboardingTourVersion?: number | null;
  legalDocsHintShown?: boolean | null;
  /** True while a newer IFU version is published than the one this user has been shown; absent means no answer (IPD-3216) */
  ifuUpdateAvailable?: boolean | null;
  minFontSize?: number;
  consents?: UserConsents;
}

/** State shape for the user store */
export interface UserStoreState {
  userData: UserData | null;
  homeContent: ContentHomeResponse | null;
}

/** Actions for managing user data, home content, and logout */
export interface UserStoreActions {
  setUserData(ur: UserResponse | null): void;
  setHomeContent(ur: ContentHomeResponse | null): void;
  logout(): void;
}

export const useUserStore =
  /** Holds the authenticated user's profile and home content, handles logout cleanup */
  defineStore<'user-store', UserStoreState, any, UserStoreActions>('user-store', {
  state: () => ({
    userData: null,
    homeContent: null,
  }),
  actions: {
    setHomeContent(chr: ContentHomeResponse | null) {
      if (chr) {
        this.homeContent = chr;
      } else {
        this.homeContent = null;
      }
    },
    setUserData(ur: UserResponse | null) {
      if (!ur) {
        this.userData = null;
        return;
      }
      this.userData = ur;
    },
    /**
     * Reactive Pinia cleanup only — does NOT touch localStorage beyond what
     * each store's `clearStore()` removes for its own keys. Auth-flow cleanup
     * (lang reset, full `localStorage.clear()`) is the caller's responsibility
     * (see `endLocalSession()` in use-auth.ts).
     */
    logout(): void {
      const bookmarksStore = useBookmarksStore();
      const weightStore = useWeightTrackingStore();
      const tourStore = useTourStore();
      const userPreferencesStore = useUserPreferencesStore();
      const viewedContentStore = useViewedContentStore();
      // setter calling is required, to trigger inner attributes
      this.setHomeContent(null);
      this.setUserData(null);
      bookmarksStore.clearStore();
      weightStore.clearStore();
      tourStore.clearStore();
      userPreferencesStore.clearStore();
      // Otherwise the next user on this device inherits the previous one's Read markers, and what
      // was already reported as watched for them.
      viewedContentStore.clearStore();
    },
  },
});
